import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Box, Typography, Alert, IconButton, Tooltip } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import { getVideoViewerStyles } from '../styles/videoStyles'
import { isVimeoUrl, getVimeoEmbedUrl, shouldUseVideoTag } from '../utils/videoUtils'

export default function VideoViewer({ url, alt }) {
  const containerRef = useRef(null)
  const videoRef = useRef(null)
  const [hasVideoError, setHasVideoError] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [userInteracted, setUserInteracted] = useState(false)

  if (!url) return null

  const styles = getVideoViewerStyles()
  const videoType = getVideoType(url)

  const enterFullscreen = useCallback(async () => {
    const container = containerRef.current
    if (!container) return

    try {
      if (container.requestFullscreen) {
        await container.requestFullscreen()
      } else if (container.webkitRequestFullscreen) {
        await container.webkitRequestFullscreen()
      } else if (container.mozRequestFullScreen) {
        await container.mozRequestFullScreen()
      } else if (container.msRequestFullscreen) {
        await container.msRequestFullscreen()
      }
    } catch (error) {
      console.warn('Failed to enter fullscreen:', error)
    }
  }, [])

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen()
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen()
      }
    } catch (error) {
      console.warn('Failed to exit fullscreen:', error)
    }
  }, [])

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen()
    } else {
      enterFullscreen()
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen])

  const handlePlayPause = useCallback(async () => {
    if (videoType === 'vimeo') {
      const iframe = containerRef.current?.querySelector('iframe')
      if (iframe && iframe.contentWindow) {
        if (isPlaying) {
          iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*')
        } else {
          iframe.contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*')
        }
      }
      setUserInteracted(true)
      return
    }

    const video = videoRef.current
    if (!video) return

    try {
      if (video.paused) {
        video.muted = false
        await video.play()
        setUserInteracted(true)
      } else {
        video.pause()
      }
    } catch (error) {
      console.error('Play/pause error:', error)
    }
  }, [videoType, isPlaying])

  const handleMuteToggle = useCallback(() => {
    const video = videoRef.current
    if (!video || videoType === 'vimeo') return

    video.muted = !video.muted
    setIsMuted(video.muted)
  }, [videoType])

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )
      setIsFullscreen(isCurrentlyFullscreen)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [])

  // Controls visibility
  useEffect(() => {
    let timeout

    const handleMouseMove = () => {
      setShowControls(true)
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        if (isPlaying && isFullscreen) {
          setShowControls(false)
        }
      }, 3000)
    }

    const handleMouseLeave = () => {
      if (isPlaying && isFullscreen) {
        setShowControls(false)
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('mousemove', handleMouseMove)
      container.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      clearTimeout(timeout)
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove)
        container.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [isPlaying, isFullscreen])

  useEffect(() => {
    if (videoType !== 'vimeo' || !url) return

    const handlePlayerMessage = event => {
      if (event.origin !== 'https://player.vimeo.com') return

      if (event.data && typeof event.data === 'string') {
        try {
          const iframe = containerRef.current?.querySelector('iframe')
          const message = JSON.parse(event.data)
          switch (message.event) {
            case 'ready':
              if (iframe) {
                iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'play' }), '*')
                iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'pause' }), '*')
                iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'ended' }), '*')
                iframe.contentWindow.postMessage(
                  JSON.stringify({ method: 'addEventListener', value: 'timeupdate' }),
                  '*'
                )
                const timeInterval = setInterval(() => {
                  if (iframe.contentWindow) {
                    iframe.contentWindow.postMessage(JSON.stringify({ method: 'getCurrentTime' }), '*')
                    iframe.contentWindow.postMessage(JSON.stringify({ method: 'getDuration' }), '*')
                  }
                }, 1000)

                iframe.timeInterval = timeInterval
              }
              break
            case 'play':
              setIsPlaying(true)
              if (message.data?.seconds !== undefined) {
                setCurrentTime(message.data.seconds)
              }
              if (message.data?.duration !== undefined) {
                setDuration(message.data.duration)
              }
              break
            case 'pause':
              setIsPlaying(false)
              if (message.data?.seconds !== undefined) {
                setCurrentTime(message.data.seconds)
              }
              if (message.data?.duration !== undefined) {
                setDuration(message.data.duration)
              }
              break
            case 'ended':
              setIsPlaying(false)
              break
            case 'timeupdate':
              if (message.data?.seconds !== undefined) {
                setCurrentTime(message.data.seconds)
              }
              if (message.data?.duration !== undefined) {
                setDuration(message.data.duration)
              }
              break
          }
        } catch (error) {
          console.error('Error parsing Vimeo message:', error)
        }
      }
    }

    window.addEventListener('message', handlePlayerMessage)
    return () => window.removeEventListener('message', handlePlayerMessage)
  }, [url, videoType])

  useEffect(() => {
    if (videoType !== 'html5' || !url) return

    const video = videoRef.current
    if (!video) return

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleEnded = () => setIsPlaying(false)
    const handleTimeUpdate = () => setCurrentTime(video.currentTime)
    const handleDurationChange = () => setDuration(video.duration)
    const handleVolumeChange = () => setIsMuted(video.muted)
    const handleLoadedMetadata = () => setDuration(video.duration)

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('durationchange', handleDurationChange)
    video.addEventListener('volumechange', handleVolumeChange)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('durationchange', handleDurationChange)
      video.removeEventListener('volumechange', handleVolumeChange)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
    }
  }, [url, videoType])

  useEffect(() => {
    return () => {
      if (videoType === 'vimeo') {
        const iframe = containerRef.current?.querySelector('iframe')
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*')
        }
      } else if (videoType === 'html5') {
        const video = videoRef.current
        if (video && !video.paused) {
          video.pause()
        }
      }
    }
  }, [])

  const handleVideoError = e => {
    console.error('Video playback error:', e)
    setHasVideoError(true)
  }

  const renderVideo = () => {
    if (shouldUseVideoTag(url)) {
      if (hasVideoError) {
        return (
          <Box sx={styles.container}>
            <Alert severity='warning' sx={{ margin: 2 }}>
              <Typography variant='body2'>
                This video format may not be supported by your browser. Please try converting to MP4 or WebM format for
                better compatibility.
              </Typography>
              <Typography variant='caption' sx={{ mt: 1, display: 'block' }}>
                File: {alt || 'Video'}
              </Typography>
            </Alert>
          </Box>
        )
      }

      return (
        <>
          <video
            ref={videoRef}
            src={url}
            style={styles.uniformVideo}
            controls={false}
            preload='metadata'
            controlsList='nodownload nofullscreen noremoteplayback'
            title={alt || 'Video'}
            onError={handleVideoError}
            onContextMenu={e => e.preventDefault()}
            disablePictureInPicture
            playsInline
          >
            <source src={url} type='video/mp4' />
            <source src={url} type='video/webm' />
            Your browser does not support the video tag.
          </video>
          <VideoControls
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onFullscreen={toggleFullscreen}
            onMuteToggle={handleMuteToggle}
            currentTime={currentTime}
            duration={duration}
            isMuted={isMuted}
            isFullscreen={isFullscreen}
            showControls={showControls}
            isVimeo={false}
          />
          {!userInteracted && !isPlaying && <Box sx={overlayStyles}></Box>}
        </>
      )
    }

    if (isVimeoUrl(url)) {
      return (
        <>
          <iframe
            src={getVimeoEmbedUrl(url)}
            style={styles.uniformIframe}
            frameBorder='0'
            allowFullScreen
            allow='autoplay; fullscreen; picture-in-picture'
            title={alt || 'Vimeo Video'}
          />
          <VideoControls
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onFullscreen={toggleFullscreen}
            onMuteToggle={handleMuteToggle}
            currentTime={currentTime}
            duration={duration}
            isMuted={isMuted}
            isFullscreen={isFullscreen}
            showControls={showControls}
            isVimeo={true}
          />
        </>
      )
    }

    return (
      <iframe src={url} style={styles.uniformIframe} frameBorder='0' allowFullScreen title={alt || 'Video Preview'} />
    )
  }

  return (
    <div ref={containerRef} style={styles.videoWrapper}>
      {renderVideo()}
    </div>
  )
}

// Video Controls Component
function VideoControls({
  isPlaying = false,
  onPlayPause,
  onFullscreen,
  onMuteToggle,
  currentTime = 0,
  duration = 0,
  isMuted = false,
  isFullscreen = false,
  showControls = true,
  isVimeo = false
}) {
  const remainingTime = duration > 0 && currentTime >= 0 ? Math.max(0, duration - currentTime) : 0

  return (
    <Box
      sx={{
        ...controlsStyles.container,
        ...(!showControls && controlsStyles.hidden)
      }}
      data-testid='video-controls'
    >
      <Box sx={controlsStyles.leftControls}>
        <Tooltip title={isPlaying ? 'Pause' : 'Play'} arrow>
          <IconButton
            onClick={onPlayPause}
            sx={controlsStyles.controlButton}
            size='small'
            data-testid='play-pause-button'
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Tooltip>

        {!isVimeo && (
          <Tooltip title={isMuted ? 'Unmute' : 'Mute'} arrow>
            <IconButton onClick={onMuteToggle} sx={controlsStyles.controlButton} size='small' data-testid='mute-button'>
              {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={controlsStyles.rightControls}>
        <Typography sx={controlsStyles.timeDisplay}>
          {duration > 0 && currentTime >= 0 ? `Remaining: ${formatTime(remainingTime)}` : 'Loading...'}
        </Typography>

        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} arrow>
          <IconButton
            onClick={onFullscreen}
            sx={controlsStyles.controlButton}
            size='small'
            data-testid='fullscreen-button'
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

// Helper functions
const getVideoType = url => {
  if (isVimeoUrl(url)) return 'vimeo'
  if (shouldUseVideoTag(url)) return 'html5'
  return 'unknown'
}

const formatTime = seconds => {
  if (isNaN(seconds) || seconds < 0) return '0:00'

  const totalSeconds = Math.floor(seconds)
  const hours = Math.floor(totalSeconds / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Overlay styles
const overlayStyles = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '12px',
  transition: 'opacity 0.3s ease',
  pointerEvents: 'none',
  zIndex: 5
}

// Controls styles
const controlsStyles = {
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
    padding: '20px 16px 16px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '0 0 12px 12px',
    transition: 'opacity 0.3s ease',
    zIndex: 10
  },
  leftControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  rightControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: '60px'
  },
  controlButton: {
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      transform: 'scale(1.05)'
    },
    '&:active': {
      transform: 'scale(0.95)'
    },
    transition: 'all 0.2s ease',
    width: '40px',
    height: '40px',
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.3)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)'
    }
  },
  timeDisplay: {
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Plus Jakarta Sans',
    minWidth: '120px',
    textAlign: 'right',
    userSelect: 'none'
  },
  hidden: {
    opacity: 0,
    pointerEvents: 'none'
  }
}
